import { notification } from 'antd';
import { useEffect, useState } from 'react';
import {
  Job,
  JobType,
  SendDailySupportEmailJobData,
  sendDailySupportEmailJobData,
} from '../api/models';
import APIClient from '../api/api';
import { formatDateTime } from '../utils';
import { Link } from 'react-router-dom';
import { Column, DataTable, Num, Pagination, StatusTag, TagTone } from '../components/ui';
import { PageFooter, PageHead, PageToolbar } from '../layout';

const DEFAULT_PAGE_SIZE = 25;

const statusTone: Record<string, TagTone> = {
  running: 'accent',
  success: 'success',
  failed: 'danger',
};

const SupportJobs = () => {
  const [jobs, setJobs] = useState<Job[]>();
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadJobs(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  async function loadJobs(p: number) {
    setLoading(true);
    try {
      const resp = await APIClient.listJobs(
        { page: p, pageSize },
        {
          type: [
            JobType.SendSupportPicksEmail,
            JobType.SendSupportSpaceshipPicksEmail,
            JobType.SendInternalAtticRenewalEmail,
          ],
        },
      );
      setJobs(resp.content);
      setTotal(resp.total);
      setPage(resp.currentPage || p);
    } catch (e) {
      notification.error({ message: 'Failed to list jobs at this time.' });
    } finally {
      setLoading(false);
    }
  }

  function getJobData(job: Job): SendDailySupportEmailJobData | undefined {
    if (job.jobData && job.jobData.length > 0) {
      try {
        return sendDailySupportEmailJobData.parse(JSON.parse(atob(job.jobData)));
      } catch {
        return undefined;
      }
    }
    return undefined;
  }

  const columns: Column<Job>[] = [
    {
      key: 'subject',
      title: 'Email Subject',
      render: (job) => (
        <Link
          to={`/emails/support-jobs/${job.id}`}
          className="text-text hover:text-accent text-[12.5px]"
        >
          {getJobData(job)?.subject || <span className="text-text-3">(no subject)</span>}
        </Link>
      ),
    },
    {
      key: 'sentAt',
      title: 'Sent At',
      width: 160,
      render: (job) => (
        <span className="font-mono text-[12px] text-text-2 tabular-nums">
          {job.finishedAt ? formatDateTime(job.finishedAt) : '—'}
        </span>
      ),
    },
    {
      key: 'responses',
      title: 'Responses',
      width: 110,
      align: 'right',
      render: (job) => <Num value={job.inboundEmails.length} format="int" />,
    },
    {
      key: 'status',
      title: 'Status',
      width: 110,
      render: (job) => (
        <StatusTag tone={statusTone[job.status] ?? 'neutral'}>{job.status}</StatusTag>
      ),
    },
  ];

  return (
    <>
      <PageHead
        title="Support Jobs"
        subtitle="Outbound support emails and the responses they received."
      />
      <PageToolbar>
        <span className="ml-auto text-[11px] text-text-3 font-mono">
          {jobs ? (
            <>
              total <b className="text-text-2">{total.toLocaleString()}</b>
            </>
          ) : (
            'loading…'
          )}
        </span>
      </PageToolbar>

      <DataTable
        columns={columns}
        data={jobs ?? []}
        rowKey={(j) => String(j.id)}
        loading={loading}
        emptyMessage="No support jobs yet."
      />
      <PageFooter
        left={
          <span>
            Showing <b className="text-text-2">{jobs?.length ?? 0}</b> of{' '}
            <b className="text-text-2">{total.toLocaleString()}</b>
          </span>
        }
        right={
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={loadJobs}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
            loading={loading}
          />
        }
      />
    </>
  );
};

export default SupportJobs;
